import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { LocationObject } from '@tradecafe/types/core'
import { Observable, OperatorFunction, from, of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { LocationsService } from 'src/pages/admin/settings/locations/locations.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { archiveLocation, createLocation, createLocationFailure, createLocationSuccess, loadLocations, loadLocationsFailure, loadLocationsSuccess, unarchiveLocation, updateLocation, updateLocationFailure, updateLocationSuccess } from './location.actions'

@Injectable()
export class LocationEffects {
  private actions$ = inject(Actions)
  private locations = inject(LocationsService)
  private toaster = inject(ToasterService)


  loadLocations$ = createEffect(() => this.actions$.pipe(
    ofType(loadLocations),
    switchMap(action =>
      from(this.locations.getLocations()).pipe(
        map(r => loadLocationsSuccess({
          tableKey: action.tableKey,
          locations: r.data,
        })),
        catchAndLog(this.toaster, 'Unable to fetch locations', loadLocationsFailure
      )))))

  reloadChangedLocations$ = createEffect(() => this.locations.locationChanged$.pipe(
    map(() => loadLocations({}))))

  createLocation$ = createEffect(() => this.actions$.pipe(
    ofType(createLocation),
    tap(() => this.toaster.progress('Creating location...')),
    switchMap(action =>
      from(this.locations.create(action.location as LocationObject)).pipe(
        map(location => createLocationSuccess({ location })),
        tap(() => this.toaster.success('Location created successfully.')),
        this.catchSaveError(createLocationFailure)))))

  updateLocation$ = createEffect(() => this.actions$.pipe(
    ofType(updateLocation),
    tap(() => this.toaster.progress('Updating location...')),
    switchMap(action =>
      from(this.locations.update({ ...action.location, location_id: action.id } as Partial<LocationObject>)).pipe(
        map(location => updateLocationSuccess({ location })),
        tap(() => this.toaster.success('Location updated successfully.')),
        this.catchSaveError(updateLocationFailure)))))

  archiveLocation$ = createEffect(() => this.actions$.pipe(
    ofType(archiveLocation),
    tap(() => this.toaster.progress('Archiving location...')),
    switchMap(action =>
      from(this.locations.archive({ location_id: action.id } as LocationObject)).pipe(
        map(location => updateLocationSuccess({ location })),
        tap(() => this.toaster.success('Location archived successfully.')),
        catchAndLog(this.toaster, 'Unable to archive location', updateLocationFailure)))))

  unarchiveLocation$ = createEffect(() => this.actions$.pipe(
    ofType(unarchiveLocation),
    tap(() => this.toaster.progress('Unarchiving location...')),
    switchMap(action =>
      from(this.locations.unarchive({ location_id: action.id } as LocationObject)).pipe(
        map(location => updateLocationSuccess({ location })),
        tap(() => this.toaster.success('Location unarchived successfully.')),
        catchAndLog(this.toaster, 'Unable to unarchive location', updateLocationFailure)))))

  private catchSaveError(failure: typeof createLocationFailure | typeof updateLocationFailure): OperatorFunction<Action, Action> {
    return catchError<Action, Observable<Action>>(err => {
      console.error('Unable to save location', err)
      if (err?.error?.error?.statusCode === 409) {
        this.toaster.error(err.error.error.message || 'Unable to save location')
      } else {
        this.toaster.error('Unable to save location')
      }
      return of(failure({ error: err }))
    })
  }
}
